import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { ReportUserFilter } from "@/components/features/reports/ReportUserFilter";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  ActionEmptyState,
  Button,
  Card,
  CardContent,
  CurrencyWrapper,
  DataTable,
  DataTableColumnHeader,
  DateFormField,
  Form,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { MatterStatusSelectItems } from "@/components/ui/FormFields/MatterStatusSelectItems";
import { StatuteConfirmedIcon } from "@/components/ui/Icons/StatuteConfirmedIcon";
import { useUser } from "@/components/UserProvider";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import {
  getUsersOptions,
  retrieveMatterSettingsQueryOptions,
} from "@/lib/data/react-query";
import { retrieveReportsOpenMattersReportQueryOptions } from "@/lib/data/react-query/reports/reports-open-matters-report";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import {
  type ReportsMatterSearchTableRow,
  type ReportsOpenMattersReport,
} from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import { mapOpenMattersReportToTableRow } from "@/lib/utils/matters/open-matters-report-utils";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import { formatReportCsvCurrencyValue } from "@/lib/utils/reports/report-csv";
import {
  REPORT_DATE_RANGE_OPTIONS,
  REPORT_DATE_RANGE_SEARCH_VALUES,
  type ReportDateRangeType,
  buildMatterStatusLabelOptions,
  formatResolvedReportDateRangeLabel,
  isReportDateRangeType,
  resolveReportDateRange,
} from "@/lib/utils/reports/report-filter-options";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FolderOpen,
  FolderSearch,
  type LucideIcon,
  Settings,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/open-matters")({
  component: RouteComponent,
  validateSearch: z.object({
    dateRangeType: z.enum(REPORT_DATE_RANGE_SEARCH_VALUES).optional(),
    dateRangeEnd: z.string().optional(),
    dateRangeStart: z.string().optional(),
    team: z.string().optional(),
    member: z.string().optional(),
    type: z.string().optional(),
    claimType: z.string().optional(),
    stage: z.string().optional(),
  }),
});
const OPEN_MATTERS_REPORT_TABLE_NAME = tanstackTableNames.reportsOpenMatters;
const OPEN_MATTERS_REPORT_PRESET_COLUMN_IDS = {
  assignees: "Assignees",
  dateOfLoss: "Date of Loss",
  insuranceClaimNumber: "Insurance Claim Number",
  insurancePolicyNumber: "Insurance Policy Number",
  lastActivity: "Last Activity",
  matterAgeDays: "Matter Age (Days)",
  medicalTreatmentBillTotal: "Matter Medical Treatment Bill Total",
  matterTitle: "Matter Title",
  matterType: "Incident Type",
  claimTypes: "Claim Types",
  openDate: "Open Date",
  stage: "Status",
  statuteOfLimitations: "Statute of Limitations",
  team: "Team",
} as const;
const OPEN_MATTERS_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(OPEN_MATTERS_REPORT_PRESET_COLUMN_IDS).map(([key, value]) => [
    value,
    key,
  ]),
) as Record<string, keyof typeof OPEN_MATTERS_REPORT_PRESET_COLUMN_IDS>;

type OpenMattersPresetFilters = {
  dateRangeEnd?: string;
  dateRangeStart?: string;
  dateRangeType?: string;
  member?: string[];
  stage?: string[];
  team?: string[];
  type?: string[];
};

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const formatMultiValueReportCsv = (
  values?: Array<string | null | undefined> | null,
) => {
  const labels = (values ?? []).filter((value): value is string =>
    Boolean(value && value.trim()),
  );
  return labels.length > 0 ? labels.join(", ") : "-";
};

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const filters = rawFilters as {
    claimTypeIds?: string[];
    matterTypeIds?: string[];
  } & OpenMattersPresetFilters;
  const dateRangeType = isReportDateRangeType(filters.dateRangeType)
    ? filters.dateRangeType
    : "ALL_TIME";

  return {
    claimType: serializeSearchValues(
      normalizePresetStringArray(filters.claimTypeIds),
    ),
    dateRangeEnd:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeEnd === "string"
        ? filters.dateRangeEnd
        : undefined,
    dateRangeStart:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeStart === "string"
        ? filters.dateRangeStart
        : undefined,
    dateRangeType,
    member: serializeSearchValues(normalizePresetStringArray(filters.member)),
    stage: serializeSearchValues(normalizePresetStringArray(filters.stage)),
    team: serializeSearchValues(normalizePresetStringArray(filters.team)),
    type: serializeSearchValues(normalizePresetStringArray(filters.type)),
  };
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { hasPagePermission } = useUser();
  const search = Route.useSearch();

  const selectedDateRange: ReportDateRangeType =
    search.dateRangeType ?? "ALL_TIME";
  const dateRangeStart = search.dateRangeStart;
  const dateRangeEnd = search.dateRangeEnd;
  const dateRangeForm = useForm<{
    dateRangeStart: string;
    dateRangeEnd: string;
  }>({
    defaultValues: {
      dateRangeStart: dateRangeStart ?? "",
      dateRangeEnd: dateRangeEnd ?? "",
    },
  });
  const watchedDateRangeStart = dateRangeForm.watch("dateRangeStart");
  const watchedDateRangeEnd = dateRangeForm.watch("dateRangeEnd");
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() =>
    parseSearchValues(search.team),
  );
  const [selectedMembers, setSelectedMembers] = useState<string[]>(() =>
    parseSearchValues(search.member),
  );
  const selectedClaimTypeIds = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    parseSearchValues(search.type),
  );
  const [selectedStages, setSelectedStages] = useState<string[]>(() =>
    parseSearchValues(search.stage),
  );
  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();
  const [typeSelectValue, setTypeSelectValue] = useState<string | undefined>();
  const [stageSelectValue, setStageSelectValue] = useState<
    string | undefined
  >();

  const addUniqueValue = (values: string[], value: string) =>
    values.includes(value) ? values : [...values, value];

  useEffect(() => {
    setSelectedTeams(parseSearchValues(search.team));
  }, [search.team]);
  useEffect(() => {
    setSelectedMembers(parseSearchValues(search.member));
  }, [search.member]);
  useEffect(() => {
    setSelectedTypes(parseSearchValues(search.type));
  }, [search.type]);
  useEffect(() => {
    setSelectedStages(parseSearchValues(search.stage));
  }, [search.stage]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    if (dateRangeStart || dateRangeEnd) {
      return;
    }
    dateRangeForm.reset(
      { dateRangeStart: "", dateRangeEnd: "" },
      { keepDefaultValues: true },
    );
  }, [dateRangeEnd, dateRangeForm, dateRangeStart, selectedDateRange]);
  useEffect(() => {
    dateRangeForm.setValue("dateRangeStart", dateRangeStart ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    dateRangeForm.setValue("dateRangeEnd", dateRangeEnd ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dateRangeEnd, dateRangeForm, dateRangeStart]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    const nextStart = watchedDateRangeStart.trim();
    const nextEnd = watchedDateRangeEnd.trim();
    if (!nextStart || !nextEnd) {
      return;
    }
    if (nextStart === dateRangeStart && nextEnd === dateRangeEnd) {
      return;
    }
    void navigate({
      search: (prev) => ({
        ...prev,
        dateRangeStart: nextStart,
        dateRangeEnd: nextEnd,
      }),
      replace: true,
    });
  }, [
    dateRangeEnd,
    dateRangeStart,
    navigate,
    selectedDateRange,
    watchedDateRangeEnd,
    watchedDateRangeStart,
  ]);
  const resolvedDateRange = useMemo(
    () =>
      resolveReportDateRange({
        dateRangeEnd,
        dateRangeStart,
        dateRangeType: selectedDateRange,
      }),
    [dateRangeEnd, dateRangeStart, selectedDateRange],
  );
  const hasActiveDateRange = Boolean(
    selectedDateRange !== "ALL_TIME" &&
      resolvedDateRange.start &&
      resolvedDateRange.end,
  );
  const canViewOpenMattersReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersAll,
  );

  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewOpenMattersReport,
  });
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    ...getUsersOptions(),
    enabled: canViewOpenMattersReport,
  });

  const teamOptions = useMemo(() => {
    const teams = matterSettingsData?.data.teams ?? [];
    const uniqueLabels = Array.from(
      new Set(
        teams
          .map((team) => team.label.trim())
          .filter((label): label is string => Boolean(label)),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const options = uniqueLabels.map((label) => ({ value: label, label }));
    const existing = new Set(options.map((option) => option.value));
    selectedTeams.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [matterSettingsData, selectedTeams]);

  const userDirectory = useUserDirectory({
    enabled: canViewOpenMattersReport,
    selectedIds: selectedMembers,
  });

  const memberOptions = useMemo(() => {
    const users = userDirectory.users;
    const mappedUsers = users
      .map((user) => {
        const nameParts = [user.firstName.trim(), user.lastName.trim()].filter(
          Boolean,
        );
        const label = nameParts.join(" ");
        return {
          value: user.id,
          label: label || user.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const options = [...mappedUsers];
    const existing = new Set(options.map((option) => option.value));
    selectedMembers.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [selectedMembers, userDirectory.users]);

  const reportFilters = useMemo(
    () => ({
      claimTypeIds: selectedClaimTypeIds,
      userId: selectedMembers,
      team: selectedTeams,
      incidentTypeId: selectedTypes.map(
        (label) =>
          (matterSettingsData?.data.incidentTypes ?? []).find(
            (incidentType) => getIncidentTypeLabel(incidentType) === label,
          )?.id ?? label,
      ),
    }),
    [
      selectedClaimTypeIds,
      selectedMembers,
      selectedTeams,
      selectedTypes,
      matterSettingsData?.data.incidentTypes,
    ],
  );

  const { data, isLoading, error } = useQuery({
    ...retrieveReportsOpenMattersReportQueryOptions(reportFilters),
    enabled: canViewOpenMattersReport,
  });

  const reports = useMemo<ReportsOpenMattersReport[]>(
    () => data?.data ?? [],
    [data],
  );
  const typeOptions = useMemo(() => {
    const types = matterSettingsData?.data.incidentTypes ?? [];
    const uniqueLabels = Array.from(
      new Set(
        types
          .map((type) => getIncidentTypeLabel(type).trim())
          .filter((label): label is string => Boolean(label)),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const options = uniqueLabels.map((label) => ({ value: label, label }));
    const existing = new Set(options.map((option) => option.value));
    selectedTypes.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [matterSettingsData, selectedTypes]);

  const stageOptions = useMemo(() => {
    return buildMatterStatusLabelOptions(
      matterSettingsData?.data.statuses,
      selectedStages,
    );
  }, [matterSettingsData?.data.statuses, selectedStages]);

  const resolveOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string,
  ) => options.find((option) => option.value === value)?.label ?? value;

  const claimFilterChips = selectedClaimTypeIds.map((id) => ({
    key: `claimType:${id}`,
    label: `Claim type: ${(matterSettingsData?.data.claimTypes ?? []).find((claimType) => claimType.id === id)?.label ?? id}`,
    onRemove: () => {
      void navigate({
        search: (prev) => ({
          ...prev,
          claimType: serializeSearchValues(
            selectedClaimTypeIds.filter((value) => value !== id),
          ),
        }),
        replace: true,
      });
    },
  }));
  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (hasActiveDateRange) {
      const label =
        formatResolvedReportDateRangeLabel({
          dateRangeEnd: resolvedDateRange.end,
          dateRangeStart: resolvedDateRange.start,
          dateRangeType: selectedDateRange,
        }) ?? selectedDateRange;
      chips.push({
        key: `dateRange:${resolvedDateRange.start ?? ""}-${resolvedDateRange.end ?? ""}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeType: "ALL_TIME",
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
            }),
            replace: true,
          });
        },
      });
    }

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          const next = selectedTeams.filter((item) => item !== value);
          setSelectedTeams(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMembers.forEach((value) => {
      chips.push({
        key: `member:${value}`,
        label: `Assignee: ${resolveOptionLabel(memberOptions, value)}`,
        onRemove: () => {
          const next = selectedMembers.filter((item) => item !== value);
          setSelectedMembers(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              member: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedTypes.forEach((value) => {
      chips.push({
        key: `type:${value}`,
        label: `Type: ${resolveOptionLabel(typeOptions, value)}`,
        onRemove: () => {
          const next = selectedTypes.filter((item) => item !== value);
          setSelectedTypes(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              type: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedStages.forEach((value) => {
      chips.push({
        key: `stage:${value}`,
        label: `Status: ${resolveOptionLabel(stageOptions, value)}`,
        onRemove: () => {
          const next = selectedStages.filter((item) => item !== value);
          setSelectedStages(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              stage: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    hasActiveDateRange,
    memberOptions,
    navigate,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedDateRange,
    selectedMembers,
    selectedStages,
    selectedTeams,
    selectedTypes,
    stageOptions,
    teamOptions,
    typeOptions,
  ]);

  const dateRangeStartValue = useMemo(() => {
    if (!resolvedDateRange.start || !dayjs(resolvedDateRange.start).isValid()) {
      return undefined;
    }
    return dayjs(resolvedDateRange.start).startOf("day");
  }, [resolvedDateRange.start]);
  const dateRangeEndValue = useMemo(() => {
    if (!resolvedDateRange.end || !dayjs(resolvedDateRange.end).isValid()) {
      return undefined;
    }
    return dayjs(resolvedDateRange.end).endOf("day");
  }, [resolvedDateRange.end]);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (
        selectedTeams.length > 0 &&
        !selectedTeams.some((team) =>
          report.openMattersReportMatterTeam?.includes(team),
        )
      ) {
        return false;
      }
      if (
        selectedMembers.length > 0 &&
        !selectedMembers.some((member) =>
          report.openMattersReportMatterAssignee?.includes(member),
        )
      ) {
        return false;
      }
      if (
        selectedTypes.length > 0 &&
        (!report.openMattersReportIncidentTypeLabel ||
          !selectedTypes.includes(report.openMattersReportIncidentTypeLabel))
      ) {
        return false;
      }
      if (
        selectedStages.length > 0 &&
        (!report.openMattersReportMatterStatusLabel ||
          !selectedStages.includes(report.openMattersReportMatterStatusLabel))
      ) {
        return false;
      }
      if (dateRangeStartValue || dateRangeEndValue) {
        const created = report.dateCreated;
        if (!created || !dayjs(created).isValid()) {
          return false;
        }
        const createdDate = dayjs(created);
        if (dateRangeStartValue && createdDate.isBefore(dateRangeStartValue)) {
          return false;
        }
        if (dateRangeEndValue && createdDate.isAfter(dateRangeEndValue)) {
          return false;
        }
      }
      return true;
    });
  }, [
    dateRangeEndValue,
    dateRangeStartValue,
    reports,
    selectedMembers,
    selectedStages,
    selectedTeams,
    selectedTypes,
  ]);

  const users = useMemo(() => usersData?.data ?? [], [usersData]);
  const rows = useMemo(() => {
    return filteredReports
      .map((report) => mapOpenMattersReportToTableRow(report, users))
      .filter((row): row is ReportsMatterSearchTableRow => row !== null);
  }, [filteredReports, users]);

  const summaryCounts = useMemo(() => {
    const counts = {
      total: rows.length,
      averageCaseAge: 0,
      touchedLast7Days: 0,
      agedOverYear: 0,
    };

    if (rows.length === 0) {
      return counts;
    }

    let totalAge = 0;
    rows.forEach((row) => {
      const created = row.dateCreated;
      if (created && dayjs(created).isValid()) {
        const age = dayjs().diff(dayjs(created), "day");
        totalAge += age;
        if (age > 365) {
          counts.agedOverYear += 1;
        }
      }

      const modified = row.dateModified;
      if (modified && dayjs(modified).isValid()) {
        const daysSinceActivity = dayjs().diff(dayjs(modified), "day");
        if (daysSinceActivity <= 7) {
          counts.touchedLast7Days += 1;
        }
      }
    });

    counts.averageCaseAge = Math.round(totalAge / rows.length);
    return counts;
  }, [rows]);

  const activeCasePercentage =
    summaryCounts.total === 0
      ? 0
      : Math.round(
          (summaryCounts.touchedLast7Days / summaryCounts.total) * 100,
        );

  const summaryCards: Array<{
    label: string;
    value: number | string;
    meta?: string;
    icon: LucideIcon;
  }> = [
    {
      label: "Total open matters",
      value: summaryCounts.total,
      icon: FolderOpen,
    },
    {
      label: "Average matter age",
      value: `${summaryCounts.averageCaseAge} days`,
      icon: CalendarClock,
    },
    {
      label: "Touched in last 7 days",
      value: summaryCounts.touchedLast7Days,
      meta: `Active matters: ${activeCasePercentage}%`,
      icon: CheckCircle2,
    },
    {
      label: "Aged over 1 year",
      value: summaryCounts.agedOverYear,
      icon: AlertTriangle,
    },
  ];

  const reportColumns = useMemo<
    ColumnDef<ReportsMatterSearchTableRow>[]
  >(() => {
    const columns: ColumnDef<ReportsMatterSearchTableRow>[] = [
      {
        accessorKey: "matterTitle",
        id: "Matter Title",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.matterTitle ?? "-",
        enableHiding: false,
      },
      {
        accessorKey: "incidentTypeLabel",
        id: "Incident Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.incidentTypeLabel || "-",
      },
      {
        accessorFn: (row) =>
          (row.claimTypes ?? []).map((claimType) => claimType.label).join(", "),
        id: "Claim Types",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <ClaimTypesCell claimTypes={row.original.claimTypes} />
        ),
      },
      {
        accessorFn: (row) => row.claimNumber,
        id: "Insurance Claim Number",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: (row) => {
          const claimNumbers = row.getValue() as string[] | null | undefined;
          return <MultiValueCell value={claimNumbers ?? []} />;
        },
      },
      {
        accessorFn: (row) => row.policyNumber,
        id: "Insurance Policy Number",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: (row) => {
          const policyNumbers = row.getValue() as string[] | null | undefined;
          return <MultiValueCell value={policyNumbers ?? []} />;
        },
      },
      {
        accessorKey: "medicalTreatmentBillTotal",
        id: "Matter Medical Treatment Bill Total",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={row.original.medicalTreatmentBillTotal ?? undefined}
          />
        ),
      },
      {
        accessorFn: (row) => row.matterDateOfLoss,
        id: "Date of Loss",
        sortingFn: "customDateTime",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: (row) => {
          const dateOfLoss = row.getValue() as string | null;
          return dateOfLoss ? formatDate(dateOfLoss) : "-";
        },
      },
      {
        accessorFn: (row) => row.matterStatuteOfLimitations,
        id: "Statute of Limitations",
        sortingFn: "customDateTime",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: (row) => {
          const statuteOfLimitations = row.getValue() as string | null;
          return statuteOfLimitations ? (
            <div className="flex items-center gap-x-2">
              {formatDate(statuteOfLimitations)}
              <StatuteConfirmedIcon
                confirmed={row.row.original.matterStatuteOfLimitationsConfirmed}
              />
            </div>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "dateCreated",
        id: "Open Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => formatDate(row.original.dateCreated) || "-",
        sortingFn: "customDateTime",
      },
      {
        id: "Matter Age (Days)",
        accessorFn: (row: ReportsMatterSearchTableRow) => row.dateCreated,
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const created = row.original.dateCreated;
          if (!created || !dayjs(created).isValid()) {
            return "-";
          }
          const age = dayjs().diff(dayjs(created), "day");
          return <span>{age}</span>;
        },
        sortingFn: "customDateTime",
      },
      {
        accessorKey: "dateModified",
        id: "Last Activity",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const modified = row.original.dateModified;
          if (!modified || !dayjs(modified).isValid()) {
            return "-";
          }
          return <span>{formatDate(modified) || "-"}</span>;
        },
        sortingFn: "customDateTime",
      },
      {
        accessorKey: "matterStatusLabel",
        id: "Status",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <span className="border-border rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {row.original.matterStatusLabel || "-"}
          </span>
        ),
      },
      {
        accessorKey: "matterAssignee",
        id: "Assignees",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const users = row.original.matterAssignee;
          const names = users
            .map((user) => `${user.firstName} ${user.lastName}`.trim())
            .filter(Boolean);
          return <MultiValueCell value={names} />;
        },
      },
      {
        accessorKey: "matterTeamConnection",
        id: "Team",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const teams = row.original.matterTeamConnection;
          const labels = teams.map((team) => team.label);
          return <MultiValueCell value={labels} />;
        },
      },
    ];

    return columns;
  }, []);

  const openMattersPresetColumnKeysById = useMemo(
    () =>
      reportColumns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") return acc;
        if (!(column.id in OPEN_MATTERS_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] = OPEN_MATTERS_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [reportColumns],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: reportColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      claimTypeIds: selectedClaimTypeIds,
      dateRangeEnd:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.end
          : undefined,
      dateRangeStart:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.start
          : undefined,
      dateRangeType: selectedDateRange,
      member: selectedMembers,
      stage: selectedStages,
      team: selectedTeams,
      type: selectedTypes,
    },
    enabled: canViewOpenMattersReport,
    lockedVisibleColumnIds: [OPEN_MATTERS_REPORT_PRESET_COLUMN_IDS.matterTitle],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: openMattersPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.openMatters,
    tableName: OPEN_MATTERS_REPORT_TABLE_NAME,
  });
  const getOpenMattersCsvValue = (
    row: ReportsMatterSearchTableRow,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Claim Types":
        return (
          (row.claimTypes ?? [])
            .map((claimType) => claimType.label)
            .join(", ") || "-"
        );
      case "Matter Title":
        return row.matterTitle ?? "-";
      case "Incident Type":
        return row.incidentTypeLabel || "-";
      case "Insurance Claim Number":
        return formatMultiValueReportCsv(row.claimNumber);
      case "Insurance Policy Number":
        return formatMultiValueReportCsv(row.policyNumber);
      case "Matter Medical Treatment Bill Total":
        return formatReportCsvCurrencyValue(row.medicalTreatmentBillTotal);
      case "Date of Loss":
        return row.matterDateOfLoss ? formatDate(row.matterDateOfLoss) : "-";
      case "Statute of Limitations":
        return row.matterStatuteOfLimitations
          ? formatDate(row.matterStatuteOfLimitations)
          : "-";
      case "Open Date":
        return formatDate(row.dateCreated) || "-";
      case "Matter Age (Days)":
        return row.dateCreated && dayjs(row.dateCreated).isValid()
          ? dayjs().diff(dayjs(row.dateCreated), "day")
          : "-";
      case "Last Activity":
        return row.dateModified && dayjs(row.dateModified).isValid()
          ? (formatDate(row.dateModified) ?? "-")
          : "-";
      case "Status":
        return row.matterStatusLabel || "-";
      case "Assignees":
        return formatMultiValueReportCsv(
          row.matterAssignee.map((user) =>
            `${user.firstName} ${user.lastName}`.trim(),
          ),
        );
      case "Team":
        return formatMultiValueReportCsv(
          row.matterTeamConnection.map((team) => team.label),
        );
      default:
        return "";
    }
  };

  if (!canViewOpenMattersReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Open matters report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={[...appliedFilterChips, ...claimFilterChips].map(
            (chip) => toReportPresetPreviewFilter(chip.label),
          )}
          saveTitle="Save open matters report preset"
          selectId="open-matters-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[...appliedFilterChips, ...claimFilterChips].map(
          (chip) => ({
            ...chip,
            onRemove: () => {
              clearSelectedPreset();
              chip.onRemove();
            },
          }),
        )}
        onClearFilters={async () => {
          clearSelectedPreset();
          dateRangeForm.reset(
            { dateRangeStart: "", dateRangeEnd: "" },
            { keepDefaultValues: true },
          );
          setSelectedTeams([]);
          setSelectedMembers([]);
          setSelectedTypes([]);
          setSelectedStages([]);
          setTeamSelectValue(undefined);
          setTypeSelectValue(undefined);
          setStageSelectValue(undefined);
          await navigate({
            search: () => ({
              dateRangeType: "ALL_TIME",
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              team: undefined,
              member: undefined,
              type: undefined,
              claimType: undefined,
              stage: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              const nextValue = value as ReportDateRangeType;
              await navigate({
                search: (prev) => ({
                  ...prev,
                  dateRangeType: nextValue,
                  dateRangeEnd:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeEnd
                      : undefined,
                  dateRangeStart:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeStart
                      : undefined,
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {REPORT_DATE_RANGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTeams, value);
              setSelectedTeams(next);
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select teams" />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportUserFilter
            {...userDirectory.selectProps}
            items={memberOptions}
            onChange={async (value) => {
              const next = addUniqueValue(selectedMembers, value);
              setSelectedMembers(next);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  member: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          />
          <ClaimTypeFilter
            value={selectedClaimTypeIds}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(value),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={typeSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTypes, value);
              setSelectedTypes(next);
              setTypeSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  type: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select types" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={stageSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedStages, value);
              setSelectedStages(next);
              setStageSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  stage: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select statuses" />
            </SelectTrigger>
            <SelectContent>
              <MatterStatusSelectItems items={stageOptions} />
            </SelectContent>
          </Select>
          {selectedDateRange === "CUSTOM_DATE_RANGE" && (
            <Form {...dateRangeForm}>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-2">
                <DateFormField
                  name="dateRangeStart"
                  label="Start date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeStart")}
                />
                <DateFormField
                  name="dateRangeEnd"
                  label="End date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeEnd")}
                />
              </div>
            </Form>
          )}
        </div>
      </ReportFilterBar>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="border-border/60 shadow-sm">
              <CardContent className="space-y-2 pt-4">
                {isLoading ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                      <div className="bg-muted h-4 w-24 animate-pulse rounded" />
                    </div>
                    <div className="bg-muted h-7 w-20 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                        <Icon className="h-5 w-5" />
                      </div>
                      <Text variant="subHeading">{card.label}</Text>
                    </div>
                    <Text variant="title" className="text-2xl leading-8">
                      {card.value}
                    </Text>
                    {card.meta ? (
                      <Text variant="subHeading" className="mt-0.5">
                        {card.meta}
                      </Text>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="space-y-4">
        <DataTable
          entityLabelPlural="matters"
          columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
          newColumnAnchors={REPORT_CLASSIFICATION_COLUMN_ANCHORS}
          key={tableRenderKey}
          hideSearchBar
          columns={reportColumns}
          data={rows}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          leftToolbarContent={
            <Text variant="body" className="text-lg font-semibold">
              Open matters
            </Text>
          }
          renderRowContextMenu={(row, trigger) => {
            const matterId = row.original.matterId;
            return matterId ? (
              <MatterRowContextMenu matterId={matterId} trigger={trigger} />
            ) : (
              trigger
            );
          }}
          onRowClick={async (row) => {
            const matterId = row.original.matterId;
            if (!matterId) return;
            await navigate({
              to: "/matters/$matterId",
              params: { matterId },
            });
          }}
          tableName={OPEN_MATTERS_REPORT_TABLE_NAME}
          isLoading={isLoading || isUsersLoading}
          isError={Boolean(error)}
          noResultsMessage="No open matters found."
          noResultsContent={
            <ActionEmptyState
              icon={<FolderSearch className="h-5 w-5" />}
              title="No open matters yet"
              description="This report will populate once your firm has open matters."
              actions={
                <>
                  <Button asChild size="sm">
                    <Link to="/matters">View matters</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/settings/matter-defaults">
                      <Settings className="h-4 w-4" />
                      View matter defaults
                    </Link>
                  </Button>
                </>
              }
            />
          }
          initialSort={{ id: "Open Date", desc: true }}
          exportCsv={{
            fileName: "open-matters-report.csv",
            getCellValue: getOpenMattersCsvValue,
          }}
        />
      </div>
    </ReportsPageLayout>
  );
}
